import { useState, useEffect } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function CountdownCalculator() {
  const [targetDate, setTargetDate] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Lifestyle", url: "https://calclab.app/lifestyle" },
      { name: "Countdown Calculator", url: "https://calclab.app/calculator/countdown" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Countdown Calculator",
      "Create countdowns to any date and time. Free online timer and countdown calculator.",
      "https://calclab.app/calculator/countdown",
      lang
    )
  ]);
  const [targetTime, setTargetTime] = useState("00:00");
  const [eventName, setEventName] = useState("");
  const [countdown, setCountdown] = useState<any>(null);

  useEffect(() => {
    if (!targetDate) return;
    
    const interval = setInterval(() => {
      const target = new Date(`${targetDate}T${targetTime}`);
      const now = new Date();
      const diff = target.getTime() - now.getTime();
      
      if (diff <= 0) {
        setCountdown({ expired: true });
        clearInterval(interval);
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds, expired: false });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [targetDate, targetTime]);

  return (
    <PageLayout calculatorName="Countdown Calculator" calculatorPath="/calculator/countdown">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Set Countdown</CardTitle>
            <CardDescription>Choose your target date and time</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Event Name (Optional)</Label>
              <Input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="My Birthday" />
            </div>
            <div>
              <Label>Target Date</Label>
              <Input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
            </div>
            <div>
              <Label>Target Time</Label>
              <Input type="time" value={targetTime} onChange={(e) => setTargetTime(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        {countdown && (
          <Card>
            <CardHeader>
              <CardTitle>{eventName || "Countdown"}</CardTitle>
            </CardHeader>
            <CardContent>
              {countdown.expired ? (
                <div className="text-center py-8">
                  <div className="text-3xl font-bold text-primary">Time's Up! 🎉</div>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-4xl font-bold text-primary">{countdown.days}</div>
                    <div className="text-sm text-muted-foreground">Days</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-primary">{countdown.hours}</div>
                    <div className="text-sm text-muted-foreground">Hours</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-primary">{countdown.minutes}</div>
                    <div className="text-sm text-muted-foreground">Minutes</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-primary">{countdown.seconds}</div>
                    <div className="text-sm text-muted-foreground">Seconds</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
